import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Article, ContactSupport, Policy, Save } from '@mui/icons-material';

const defaultPages = {
  terms: {
    title: 'Terms & Conditions',
    content: '',
    lastUpdated: 'Jan 2026',
  },
  privacy: {
    title: 'Privacy Policy',
    content: '',
    lastUpdated: 'Jan 2026',
  },
  support: {
    title: 'Contact Support',
    intro: '',
    email: '',
    phone: '',
    hours: '',
  },
};

const CustomerCmsSettings = () => {
  const [pages, setPages] = useState(defaultPages);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadPages = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const { data } = await axios.get('/admin/settings/customer-cms');
      if (data.success) {
        setPages({
          terms: { ...defaultPages.terms, ...data.pages?.terms },
          privacy: { ...defaultPages.privacy, ...data.pages?.privacy },
          support: { ...defaultPages.support, ...data.pages?.support },
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load customer CMS content',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const updatePage = (section, field, value) => {
    setPages((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: value,
      },
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const { data } = await axios.post('/admin/settings/customer-cms', { pages });
      setPages({
        terms: { ...defaultPages.terms, ...data.pages?.terms },
        privacy: { ...defaultPages.privacy, ...data.pages?.privacy },
        support: { ...defaultPages.support, ...data.pages?.support },
      });
      setMessage({ type: 'success', text: data.message || 'Customer CMS content saved' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save customer CMS content',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#116611' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Article sx={{ color: '#116611', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
              Customer App CMS
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Edit the Terms, Privacy Policy, and Support content shown inside the customer app.
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSave}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Article sx={{ color: '#116611' }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Terms & Conditions
                </Typography>
              </Stack>
              <TextField
                fullWidth
                label="Title"
                value={pages.terms.title}
                onChange={(event) => updatePage('terms', 'title', event.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Last updated"
                value={pages.terms.lastUpdated}
                onChange={(event) => updatePage('terms', 'lastUpdated', event.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Content"
                value={pages.terms.content}
                onChange={(event) => updatePage('terms', 'content', event.target.value)}
                margin="normal"
                multiline
                minRows={12}
                required
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Policy sx={{ color: '#116611' }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Privacy Policy
                </Typography>
              </Stack>
              <TextField
                fullWidth
                label="Title"
                value={pages.privacy.title}
                onChange={(event) => updatePage('privacy', 'title', event.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Last updated"
                value={pages.privacy.lastUpdated}
                onChange={(event) => updatePage('privacy', 'lastUpdated', event.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Content"
                value={pages.privacy.content}
                onChange={(event) => updatePage('privacy', 'content', event.target.value)}
                margin="normal"
                multiline
                minRows={12}
                required
              />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <ContactSupport sx={{ color: '#116611' }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Contact Support
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={pages.support.title}
                    onChange={(event) => updatePage('support', 'title', event.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Support hours"
                    value={pages.support.hours}
                    onChange={(event) => updatePage('support', 'hours', event.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Intro"
                    value={pages.support.intro}
                    onChange={(event) => updatePage('support', 'intro', event.target.value)}
                    multiline
                    minRows={3}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={pages.support.email}
                    onChange={(event) => updatePage('support', 'email', event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={pages.support.phone}
                    onChange={(event) => updatePage('support', 'phone', event.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
          >
            {saving ? 'Saving...' : 'Save CMS Content'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default CustomerCmsSettings;
